import React, { useEffect, useState } from 'react';
import { catalogApi } from '../../api/catalog';
import ChatPanel from './ChatPanel';

/**
 * Wizard BA híbrido: preguntas del catálogo paso a paso + chat IA para profundizar
 */
export default function BAWizard({ onComplete, onBack, loading, error }) {
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [projectName, setProjectName] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const response = await catalogApi.getQuestions(true);
      setQuestions(response.data);
    } catch (err) {
      console.error('Error loading questions:', err);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const currentQuestion = questions[currentStep];
  const progress = questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;
  const isLastStep = currentStep === questions.length - 1;

  const handleAnswer = (answer) => {
    setAnswers({ ...answers, [currentStep]: answer });
  };

  const handleNext = () => {
    if (isLastStep) {
      // Generate full description from answers
      const description = generateDescription();
      onComplete(description, projectName || 'Proyecto sin nombre');
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateDescription = () => {
    const parts = [];
    questions.forEach((q, idx) => {
      if (answers[idx]) {
        parts.push(`**${q.category} — ${q.question}**\n${answers[idx]}`);
      }
    });
    return parts.join('\n\n');
  };

  if (loadingQuestions) {
    return <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>;
  }

  if (questions.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
        <p className="text-gray-500">No hay preguntas en el catálogo. Configúralas en el panel de administración.</p>
        <button onClick={onBack} className="mt-4 text-blue-600 hover:underline text-sm">← Volver</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <button onClick={onBack} className="text-gray-400 hover:text-gray-600 text-sm">← Volver</button>
          <span className="text-sm text-gray-500">
            Pregunta {currentStep + 1} de {questions.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Project name (first step) */}
      {currentStep === 0 && !projectName && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del proyecto</label>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            placeholder="Nombre del proyecto"
          />
        </div>
      )}

      {/* Question card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="mb-1">
          <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
            {currentQuestion?.category}
          </span>
        </div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">{currentQuestion?.question}</h3>

        <textarea
          value={answers[currentStep] || ''}
          onChange={(e) => handleAnswer(e.target.value)}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-y"
          placeholder="Tu respuesta..."
          autoFocus
        />

        {/* Deepen with AI */}
        <button
          onClick={() => setShowChat(!showChat)}
          className="mt-3 text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
        >
          {showChat ? '✕ Cerrar chat IA' : '🤖 Profundizar con IA'}
        </button>

        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
        )}
      </div>

      {/* Chat panel (expandable) */}
      {showChat && (
        <ChatPanel
          contextQuestion={currentQuestion?.question}
          currentAnswer={answers[currentStep] || ''}
          onUpdateAnswer={(newAnswer) => handleAnswer(newAnswer)}
        />
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePrev}
          disabled={currentStep === 0}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ← Anterior
        </button>
        <button
          onClick={handleNext}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
        >
          {loading ? '🔄 Analizando...' : isLastStep ? '🚀 Analizar proyecto' : 'Siguiente →'}
        </button>
      </div>
    </div>
  );
}
